import React, {useState, useRef, useMemo} from 'react';
import {useChatContext} from '../context';
import {Autocomplete, TextField, InputAdornment} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {useTranslation} from 'react-i18next';
import {Chat} from '@services/Chat/types';
import {ContactOptionItem} from './ContactOptionItem';

type Option = string | Chat;

export const ChatListSearchRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Option,
) => {
    return (
        <li {...props}>
            <ContactOptionItem
                option={option}
                size={24}
            />
        </li>
    );
};

export const ChatListSearch = () => {
    const {t} = useTranslation('common');
    const {chatUsers, chats, getPrivateChat, setSelectedChat} = useChatContext();

    const filteredUsers = useMemo(() => {
        const privateTitles = new Set(
            chats.filter((c) => c.type === 'PRIVATE').map((c) => c.title),
        );
        return (chatUsers ?? []).filter((u) => !privateTitles.has(u));
    }, [chatUsers, chats]);

    const options = useMemo<Option[]>(
        () => [...filteredUsers, ...(chats ?? [])],
        [filteredUsers, chats],
    );

    const [value, setValue] = useState<Option | null>(null);
    const [inputValue, setInputValue] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <Autocomplete
            fullWidth
            autoHighlight
            options={options}
            value={value}
            inputValue={inputValue}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
            isOptionEqualToValue={(option, value) =>
                typeof option === 'string'
                    ? option === (value as string)
                    : option.id === (value as Chat).id
            }
            onChange={(_, newValue) => {
                if (!newValue) return;
                if (typeof newValue === 'string') getPrivateChat(newValue);
                else setSelectedChat(newValue);
                setValue(null);
                setInputValue('');
                inputRef.current?.blur();
            }}
            onInputChange={(_, newInput, reason) => {
                if (reason === 'input') setInputValue(newInput);
                else if (reason === 'clear') setInputValue('');
            }}
            popupIcon={null}
            slotProps={{
                popper: {
                    sx: {
                        width: '100%',
                    },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={t('search')}
                    variant='outlined'
                    size='small'
                    inputRef={inputRef}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <InputAdornment position='start'>
                                    <SearchIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {border: 'none'},
                            '&:hover fieldset': {border: 'none'},
                            '&.Mui-focused fieldset': {border: 'none'},
                            backgroundColor: 'var(--mui-palette-background-paper)',
                        },
                    }}
                />
            )}
            renderOption={ChatListSearchRenderOption}
        />
    );
};
